import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Tab,
	Tabs,
	TextField,
	Tooltip
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderToolbar
} from "../../../../_metronic/_partials/controls";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { buttonsStyle, dateFormatter } from "../../../components/tables/table";
import {
	getSessionState,
	controlNext,
	controlPrev,
	controlGoto,
	controlReset,
	controlSimulate,
	controlClearSimulated
} from "../../../../api/session";
import { updateTable, deleteTable } from "../../../../api/table";
import {
	updateContribution,
	deleteContribution
} from "../../../../api/contribution";
import {
	getExpectationsBySession,
	deleteExpectation
} from "../../../../api/expectation";
import {
	getSurveyResponsesBySession,
	deleteSurveyResponse
} from "../../../../api/survey";
import { getReportJson, downloadReportXlsx } from "../../../../api/report";
import { alertError, alertInfo, alertSuccess } from "../../../../utils/logger";
import { csvDate, downloadCsv, safeFileName } from "../../../../utils/csv";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { useIsAdmin } from "../../../hooks/useIsAdmin";
import {
	COLORS,
	tint,
	StateBadge,
	LONG_TEXT_STYLE
} from "../../../components/palette";
import {
	ROUNDS,
	OPENING_ROUND,
	phaseLabel,
	computeKpis,
	surveyStats,
	questionLabel
} from "./sessionKpis";
import SummaryTab, { SurveySummary } from "./SummaryTab";

const POLL_MS = 5000;
const RESPONSE_MAX_CHARS = 500;

/** Pestañas del seguimiento (valor estable para poder enlazar desde el Resumen). */
const TABS = {
	summary: "summary",
	tables: "tables",
	responses: "responses",
	surprise: "surprise",
	expectations: "expectations",
	survey: "survey"
};

function fmtDate(value) {
	if (!value) return "—";
	return dateFormatter(value);
}

export default function SessionDetailPage() {
	const sessionId = useParams().id;
	const history = useHistory();
	const isAdmin = useIsAdmin();
	const [snapshot, setSnapshot] = useState(null);
	const [surveyResponses, setSurveyResponses] = useState([]);
	const [expectations, setExpectations] = useState([]);
	const [tab, setTab] = useState(TABS.summary);
	const [gotoPhase, setGotoPhase] = useState("");
	const [busy, setBusy] = useState(false);
	const [confirm, setConfirm] = useState(null); // { title, text, onConfirm }
	const [tableDialog, setTableDialog] = useState(null); // { id, name }
	const [responseDialog, setResponseDialog] = useState(null); // { id, text, table, round }
	const [reportJson, setReportJson] = useState(null);
	const mounted = useRef(true);

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton
	} = useSkeleton();

	/* ---------- Carga y refresco ---------- */

	const fetchState = useCallback(() => {
		return getSessionState(sessionId)
			.then(res => {
				if (res.status === 200 && mounted.current) {
					setSnapshot(res.data);
					setGotoPhase(prev => prev || res.data?.session?.phase || "");
				}
			})
			.catch(error => {
				alertError({
					error: error,
					customMessage: "No se ha podido cargar el estado de la sesión."
				});
				history.push("/sessions");
			});
	}, [sessionId, history]);

	const fetchSurvey = useCallback(() => {
		return getSurveyResponsesBySession(sessionId)
			.then(res => {
				if (res.status === 200 && mounted.current)
					setSurveyResponses(res.data || []);
			})
			.catch(() => {
				/* silencioso: el contador del snapshot sigue siendo válido */
			});
	}, [sessionId]);

	const fetchExpectations = useCallback(() => {
		return getExpectationsBySession(sessionId)
			.then(res => {
				if (res.status === 200 && mounted.current)
					setExpectations(res.data || []);
			})
			.catch(() => {
				/* silencioso: el contador del snapshot sigue siendo válido */
			});
	}, [sessionId]);

	const refreshAll = useCallback(() => {
		return Promise.all([fetchState(), fetchSurvey(), fetchExpectations()]);
	}, [fetchState, fetchSurvey, fetchExpectations]);

	useEffect(() => {
		mounted.current = true;
		refreshAll().then(() => disableLoadingData());
		const interval = setInterval(refreshAll, POLL_MS);
		return () => {
			mounted.current = false;
			clearInterval(interval);
		};
	}, [refreshAll, disableLoadingData]);

	/* ---------- Helpers sobre el snapshot ---------- */

	const session = snapshot?.session || {};
	const content = snapshot?.content || {};
	const tables = snapshot?.tables || [];
	const rounds = snapshot?.rounds || {};
	const responseMaxChars = content.response?.maxChars || RESPONSE_MAX_CHARS;
	const responseLabel = content.response?.label || "Respuesta de la mesa";

	const tableName = id => {
		const t = tables.find(x => String(x._id) === String(id));
		if (!t) return "Mesa desconocida";
		return t.name || `Mesa ${t.slot}`;
	};

	const roundContent = n =>
		(content.rounds || []).find(r => r.index === n) ||
		(content.rounds || [])[n - 1] ||
		{};

	/* ---------- Acciones de control ---------- */

	function runAction(promiseFactory, okMessage, errMessage) {
		setBusy(true);
		return promiseFactory()
			.then(res => {
				if (okMessage)
					alertSuccess({
						customMessage:
							typeof okMessage === "function" ? okMessage(res) : okMessage
					});
				return refreshAll();
			})
			.catch(error => {
				alertError({ error: error, customMessage: errMessage });
			})
			.finally(() => {
				if (mounted.current) setBusy(false);
			});
	}

	const handlePrev = () =>
		runAction(
			() => controlPrev(sessionId),
			null,
			"No se ha podido retroceder de fase."
		);

	const handleNext = () =>
		runAction(
			() => controlNext(sessionId),
			null,
			"No se ha podido avanzar de fase."
		);

	const handleGoto = () => {
		if (!gotoPhase) return;
		runAction(
			() => controlGoto(sessionId, gotoPhase),
			null,
			"No se ha podido cambiar de fase."
		);
	};

	const handleReset = () =>
		runAction(
			() => controlReset(sessionId, { keepTables: true }),
			"Dinámica reiniciada: la sesión vuelve a la apertura.",
			"No se ha podido reiniciar la dinámica."
		);

	const handleSimulate = () =>
		runAction(
			() => controlSimulate(sessionId, { what: "auto" }),
			res => {
				const s = res?.data?.summary || res?.data || {};
				const parts = [];
				if (s.tablesCreated != null) parts.push(`${s.tablesCreated} mesas`);
				if (s.answers != null) parts.push(`${s.answers} datos clave`);
				if (s.contributions != null)
					parts.push(`${s.contributions} respuestas`);
				return parts.length
					? `Simulación creada: ${parts.join(", ")}.`
					: "Simulación creada.";
			},
			"No se ha podido simular."
		);

	const handleClearSimulated = () =>
		runAction(
			() => controlClearSimulated(sessionId),
			"Mesas simuladas eliminadas.",
			"No se han podido quitar las mesas simuladas."
		);

	const handleDownloadXlsx = () => {
		setBusy(true);
		downloadReportXlsx(sessionId, `informe-${session.code || sessionId}.xlsx`)
			.catch(error => {
				alertError({
					error: error,
					customMessage: "No se ha podido descargar el informe."
				});
			})
			.finally(() => {
				if (mounted.current) setBusy(false);
			});
	};

	const handleShowJson = () => {
		getReportJson(sessionId)
			.then(res => {
				if (res.status === 200) setReportJson(res.data);
			})
			.catch(error => {
				alertError({
					error: error,
					customMessage: "No se ha podido obtener el informe."
				});
			});
	};

	/* ---------- CSV generados en cliente desde el snapshot ---------- */

	const handleDownloadResponsesCsv = () => {
		const rows = [];
		ROUNDS.forEach(n => {
			const rc = roundContent(n);
			const label = `Eje ${n} · ${rc.shortTitle || rc.title || ""}`.trim();
			sortResponsesByTable(tables, rounds[n]?.responses || []).forEach(c =>
				rows.push([label, tableName(c.table), c.text])
			);
		});
		if (!rows.length) {
			alertInfo({ customMessage: "Todavía no hay respuestas de mesa." });
			return;
		}
		downloadCsv(
			`respuestas-${safeFileName(session.code || sessionId)}.csv`,
			["Eje", "Mesa", "Respuesta"],
			rows
		);
	};

	const handleDownloadExpectationsCsv = () => {
		if (!expectations.length) {
			alertInfo({
				customMessage:
					(snapshot?.expectations?.responses || 0) > 0
						? "No se han podido cargar las expectativas. Vuelve a intentarlo en unos segundos."
						: "Todavía no hay respuestas a la pregunta de inicio."
			});
			return;
		}
		downloadCsv(
			`expectativas-${safeFileName(session.code || sessionId)}.csv`,
			["Fecha", "Expectativas"],
			expectations.map(e => [csvDate(e.createdAt), e.text])
		);
	};

	/* ---------- Mesas ---------- */

	const saveTableName = () => {
		if (!tableDialog) return;
		const { id, name } = tableDialog;
		setTableDialog(null);
		runAction(
			() => updateTable(id, { name: name.trim() }),
			"Nombre de la mesa guardado.",
			"No se ha podido renombrar la mesa."
		);
	};

	const askDeleteTable = t =>
		setConfirm({
			title: `¿Eliminar la mesa "${t.name || `Mesa ${t.slot}`}"?`,
			text:
				"Se borrarán también sus datos clave y sus respuestas. La tablet podrá volver a unirse con el mismo número de mesa.",
			onConfirm: () =>
				runAction(
					() => deleteTable(t._id),
					"Mesa eliminada.",
					"No se ha podido eliminar la mesa."
				)
		});

	/* ---------- Respuestas de mesa ---------- */

	const saveResponse = () => {
		if (!responseDialog) return;
		const { id, text } = responseDialog;
		// Conservamos los saltos de línea; solo compactamos espacios y recortamos
		const clean = (text || "")
			.replace(/[ \t]+/g, " ")
			.replace(/\n{3,}/g, "\n\n")
			.trim();
		if (!clean) {
			alertError({
				error: null,
				customMessage:
					"La respuesta no puede quedar vacía. Si quieres quitarla, usa «Eliminar»."
			});
			return;
		}
		if (clean.length > responseMaxChars) {
			alertError({
				error: null,
				customMessage: `Máximo ${responseMaxChars} caracteres.`
			});
			return;
		}
		setResponseDialog(null);
		runAction(
			() => updateContribution(id, { text: clean }),
			"Respuesta corregida.",
			"No se ha podido guardar la respuesta."
		);
	};

	const askDeleteResponse = c =>
		setConfirm({
			title: `¿Eliminar la respuesta de ${tableName(c.table)}?`,
			text: c.text.length > 160 ? `"${c.text.slice(0, 160)}…"` : `"${c.text}"`,
			onConfirm: () =>
				runAction(
					() => deleteContribution(c._id),
					"Respuesta eliminada.",
					"No se ha podido eliminar la respuesta."
				)
		});

	/* ---------- Expectativas ---------- */

	const askDeleteExpectation = e =>
		setConfirm({
			title: "¿Eliminar esta expectativa?",
			text: e.text.length > 160 ? `"${e.text.slice(0, 160)}…"` : `"${e.text}"`,
			onConfirm: () =>
				runAction(
					() => deleteExpectation(e._id),
					"Expectativa eliminada.",
					"No se ha podido eliminar la expectativa."
				)
		});

	/* ---------- Encuesta ---------- */

	const askDeleteSurveyResponse = r =>
		setConfirm({
			title: "¿Eliminar esta respuesta de la encuesta?",
			text: `Enviada el ${fmtDate(r.createdAt)}.`,
			onConfirm: () =>
				runAction(
					() => deleteSurveyResponse(r._id),
					"Respuesta eliminada.",
					"No se ha podido eliminar la respuesta."
				)
		});

	const surveyQuestions = content.survey?.questions || [];
	const survey = surveyStats(surveyQuestions, surveyResponses);

	/* ---------- Render ---------- */

	if (isLoadingData || !snapshot) return <ContentSkeleton />;

	const kpis = computeKpis(snapshot);
	const connectedCount = kpis.tables.connected;
	const totalResponses = kpis.totalResponses;
	const totalExpectations =
		snapshot.expectations?.responses ?? expectations.length;

	return (
		<>
			{/* ---------- Cabecera ---------- */}
			<Card>
				<CardHeader
					title={
						<span>
							{session.name}{" "}
							<span className="text-muted font-size-sm ml-2">
								{session.code}
							</span>
							{session.active && (
								<StateBadge color={COLORS.green} className="ml-3">
									Activa
								</StateBadge>
							)}
							{session.demo && (
								<StateBadge color={COLORS.lightBlue} className="ml-2">
									Demo
								</StateBadge>
							)}
						</span>
					}
				>
					<CardHeaderToolbar>
						<Button
							variant="outlined"
							size="small"
							onClick={() => history.push("/sessions")}
							style={{ marginRight: "10px" }}
						>
							Volver
						</Button>
						{isAdmin && (
							<Button
								variant="outlined"
								size="small"
								onClick={() => history.push("/edit-session/" + sessionId)}
							>
								Editar sesión
							</Button>
						)}
					</CardHeaderToolbar>
				</CardHeader>
				<CardBody>
					<div className="row">
						<div className="col-12 col-md-3 mb-4">
							<div className="text-muted font-size-sm">Fase actual</div>
							<div className="font-size-h4 font-weight-bolder">
								{phaseLabel(session.phase)}
							</div>
							<div className="text-muted font-size-sm">
								{session.phase} · {(session.phaseIndex ?? 0) + 1}/
								{(session.phases || []).length} · desde{" "}
								{fmtDate(session.phaseStartedAt)}
							</div>
						</div>
						<div className="col-12 col-md-3 mb-4">
							<div className="text-muted font-size-sm">Ojo del Daruma</div>
							<div className="font-size-h4 font-weight-bolder">
								{session.eyeProgress ?? 0} %
							</div>
							<div className="progress" style={{ height: "8px" }}>
								<div
									className="progress-bar bg-primary"
									role="progressbar"
									style={{ width: `${session.eyeProgress || 0}%` }}
									aria-valuenow={session.eyeProgress || 0}
									aria-valuemin="0"
									aria-valuemax="100"
								/>
							</div>
						</div>
						<div className="col-12 col-md-3 mb-4">
							<div className="text-muted font-size-sm">Mesas conectadas</div>
							<div className="font-size-h4 font-weight-bolder">
								{connectedCount} / {session.numTables}
							</div>
							<div className="text-muted font-size-sm">
								{tables.length} mesas registradas ·{" "}
								{snapshot.presence?.screens || 0} pantalla(s) ·{" "}
								{snapshot.presence?.controls || 0} control(es)
							</div>
						</div>
						<div className="col-12 col-md-3 mb-4">
							<div className="text-muted font-size-sm">Entradas</div>
							<div className="font-size-h4 font-weight-bolder">
								{totalResponses} respuestas de mesa
							</div>
							<div className="text-muted font-size-sm">
								{totalExpectations} respuestas a la pregunta de inicio ·{" "}
								{snapshot.survey?.responses || 0} respuestas de encuesta
							</div>
						</div>
					</div>

					<div className="separator separator-dashed my-4" />

					{!isAdmin && (
						<div className="text-muted font-size-sm mb-3">
							Acceso de solo lectura: puedes consultar los datos y descargar
							los informes.
						</div>
					)}

					{/* Controles de fase (solo administradores) */}
					{isAdmin && (
					<div className="d-flex flex-wrap align-items-center">
						<Button
							variant="contained"
							color="default"
							disabled={busy || (session.phaseIndex ?? 0) <= 0}
							onClick={handlePrev}
							className="mr-3 mb-3"
						>
							◀ Fase anterior
						</Button>
						<Button
							variant="contained"
							color="primary"
							disabled={
								busy ||
								(session.phaseIndex ?? 0) >= (session.phases || []).length - 1
							}
							onClick={handleNext}
							className="mr-3 mb-3"
						>
							Fase siguiente ▶
						</Button>
						<FormControl
							variant="outlined"
							size="small"
							className="mr-2 mb-3"
							style={{ minWidth: "260px" }}
						>
							<InputLabel id="goto-phase-label">Ir a fase</InputLabel>
							<Select
								labelId="goto-phase-label"
								label="Ir a fase"
								value={gotoPhase}
								onChange={e => setGotoPhase(e.target.value)}
							>
								{(session.phases || []).map((p, i) => (
									<MenuItem key={p} value={p}>
										{i + 1}. {phaseLabel(p)}{" "}
										<span className="text-muted ml-2">({p})</span>
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Button
							variant="outlined"
							color="primary"
							disabled={busy || !gotoPhase || gotoPhase === session.phase}
							onClick={handleGoto}
							className="mr-3 mb-3"
						>
							Ir
						</Button>
					</div>
					)}

					{/* Otras acciones */}
					<div className="d-flex flex-wrap align-items-center">
						{isAdmin && (
						<>
						<Button
							variant="outlined"
							color="secondary"
							disabled={busy}
							onClick={() =>
								setConfirm({
									title: "¿Reiniciar la dinámica?",
									text:
										"Se borrarán los datos clave y las respuestas de todas las mesas y la sesión volverá a la apertura. Las mesas se conservan. La encuesta y las expectativas no se tocan.",
									onConfirm: handleReset
								})
							}
							className="mr-3 mb-3"
						>
							Reiniciar dinámica
						</Button>
						<Button
							variant="outlined"
							disabled={busy}
							onClick={handleSimulate}
							className="mr-3 mb-3"
						>
							Simular mesas/respuestas
						</Button>
						<Button
							variant="outlined"
							disabled={busy || !tables.some(t => t.simulated)}
							onClick={() =>
								setConfirm({
									title: "¿Quitar las mesas simuladas?",
									text: "Se eliminarán las mesas simuladas y todos sus datos.",
									onConfirm: handleClearSimulated
								})
							}
							className="mr-3 mb-3"
						>
							Quitar simuladas
						</Button>
						</>
						)}
						<Button
							variant="contained"
							color="primary"
							disabled={busy}
							onClick={handleDownloadXlsx}
							className="mr-3 mb-3"
						>
							Descargar informe (Excel)
						</Button>
						<Button
							variant="outlined"
							color="primary"
							onClick={handleDownloadResponsesCsv}
							className="mr-3 mb-3"
						>
							Descargar respuestas (CSV)
						</Button>
						<Button
							variant="outlined"
							color="primary"
							onClick={handleDownloadExpectationsCsv}
							className="mr-3 mb-3"
						>
							Descargar expectativas (CSV)
						</Button>
						<Button
							variant="outlined"
							disabled={busy}
							onClick={handleShowJson}
							className="mr-3 mb-3"
						>
							Ver informe JSON
						</Button>
						<span className="text-muted font-size-sm mb-3">
							Actualizado: {fmtDate(snapshot.serverTime)} (cada {POLL_MS / 1000}{" "}
							s)
						</span>
					</div>
				</CardBody>
			</Card>

			{/* ---------- Pestañas ---------- */}
			<Card>
				<CardBody>
					<Tabs
						value={tab}
						onChange={(e, v) => setTab(v)}
						indicatorColor="primary"
						textColor="primary"
						variant="scrollable"
						scrollButtons="auto"
					>
						<Tab value={TABS.summary} label="Resumen" />
						<Tab value={TABS.tables} label={`Mesas (${tables.length})`} />
						<Tab
							value={TABS.responses}
							label={`Respuestas (${totalResponses})`}
						/>
						<Tab value={TABS.surprise} label="Dato clave" />
						<Tab
							value={TABS.expectations}
							label={`Expectativas (${totalExpectations})`}
						/>
						<Tab
							value={TABS.survey}
							label={`Encuesta (${snapshot.survey?.responses || 0})`}
						/>
					</Tabs>
					<div className="mt-6">
						{tab === TABS.summary && (
							<SummaryTab
								kpis={kpis}
								survey={survey}
								onGoToTab={key => setTab(TABS[key] || TABS.summary)}
							/>
						)}
						{tab === TABS.tables && (
							<TablesTab
								tables={tables}
								canEdit={isAdmin}
								onRename={t =>
									setTableDialog({ id: t._id, name: t.name || "" })
								}
								onDelete={askDeleteTable}
							/>
						)}
						{tab === TABS.responses && (
							<ResponsesTab
								rounds={rounds}
								tables={tables}
								roundContent={roundContent}
								tableName={tableName}
								canEdit={isAdmin}
								onEdit={(c, n) =>
									setResponseDialog({
										id: c._id,
										text: c.text,
										table: c.table,
										round: n
									})
								}
								onDelete={askDeleteResponse}
							/>
						)}
						{tab === TABS.surprise && (
							<SurpriseTab
								rounds={rounds}
								content={content}
								roundContent={roundContent}
								tableName={tableName}
							/>
						)}
						{tab === TABS.expectations && (
							<ExpectationsTab
								question={content.expectations?.question}
								items={expectations}
								total={totalExpectations}
								canEdit={isAdmin}
								onDelete={askDeleteExpectation}
							/>
						)}
						{tab === TABS.survey && (
							<SurveyTab
								questions={surveyQuestions}
								responses={surveyResponses}
								stats={survey}
								canEdit={isAdmin}
								onDelete={askDeleteSurveyResponse}
							/>
						)}
					</div>
				</CardBody>
			</Card>

			{/* ---------- Diálogos ---------- */}
			<ConfirmDialog
				title={confirm?.title || ""}
				open={!!confirm}
				setOpen={open => {
					if (!open) setConfirm(null);
				}}
				onConfirm={() => {
					if (confirm?.onConfirm) confirm.onConfirm();
				}}
			>
				{confirm?.text}
			</ConfirmDialog>

			<Dialog
				open={!!tableDialog}
				onClose={() => setTableDialog(null)}
				fullWidth
				maxWidth="xs"
			>
				<DialogTitle>Renombrar mesa</DialogTitle>
				<DialogContent>
					<TextField
						label="Nombre de la mesa"
						value={tableDialog?.name || ""}
						onChange={e =>
							setTableDialog({ ...tableDialog, name: e.target.value })
						}
						inputProps={{ maxLength: 40 }}
						helperText="Máximo 40 caracteres. Vacío = «Mesa {nº}»."
						InputLabelProps={{ shrink: true }}
						margin="normal"
						variant="outlined"
						fullWidth
						autoFocus
					/>
				</DialogContent>
				<DialogActions>
					<Button variant="outlined" onClick={() => setTableDialog(null)}>
						Cancelar
					</Button>
					<Button variant="outlined" color="primary" onClick={saveTableName}>
						Guardar
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={!!responseDialog}
				onClose={() => setResponseDialog(null)}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle>
					Corregir respuesta
					{responseDialog && (
						<span className="text-muted font-size-sm ml-2">
							Eje {responseDialog.round} · {tableName(responseDialog.table)}
						</span>
					)}
				</DialogTitle>
				<DialogContent>
					{responseDialog && roundContent(responseDialog.round).question && (
						<div className="text-muted font-italic mb-2">
							{roundContent(responseDialog.round).question}
						</div>
					)}
					<TextField
						label={responseLabel}
						value={responseDialog?.text || ""}
						onChange={e =>
							setResponseDialog({
								...responseDialog,
								text: e.target.value
							})
						}
						inputProps={{ maxLength: responseMaxChars }}
						helperText={`${
							(responseDialog?.text || "").length
						}/${responseMaxChars} caracteres`}
						InputLabelProps={{ shrink: true }}
						margin="normal"
						variant="outlined"
						multiline
						rows={6}
						fullWidth
						autoFocus
					/>
				</DialogContent>
				<DialogActions>
					<Button variant="outlined" onClick={() => setResponseDialog(null)}>
						Cancelar
					</Button>
					<Button variant="outlined" color="primary" onClick={saveResponse}>
						Guardar
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={!!reportJson}
				onClose={() => setReportJson(null)}
				fullWidth
				maxWidth="md"
			>
				<DialogTitle>Informe (JSON)</DialogTitle>
				<DialogContent>
					<pre
						style={{
							fontSize: "11px",
							maxHeight: "70vh",
							overflow: "auto",
							background: "#f3f6f9",
							padding: "12px"
						}}
					>
						{reportJson ? JSON.stringify(reportJson, null, 2) : ""}
					</pre>
				</DialogContent>
				<DialogActions>
					<Button variant="outlined" onClick={() => setReportJson(null)}>
						Cerrar
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

/* ====================================================================== */
/* Pestañas                                                               */
/* ====================================================================== */

function EmptyNotice({ children }) {
	return <div className="text-muted py-4">{children}</div>;
}

function TablesTab({ tables, canEdit, onRename, onDelete }) {
	if (!tables.length)
		return <EmptyNotice>Todavía no se ha unido ninguna mesa.</EmptyNotice>;
	return (
		<div className="table-responsive">
			<table className="table table-head-custom table-vertical-center">
				<thead>
					<tr>
						<th style={{ width: "60px" }}>Nº</th>
						<th>Nombre</th>
						<th style={{ width: "110px" }}>Conectada</th>
						<th>Participantes</th>
						<th style={{ width: "100px" }}>Simulada</th>
						<th style={{ width: "160px" }}>Última señal</th>
						{canEdit && <th style={{ width: "120px" }}></th>}
					</tr>
				</thead>
				<tbody>
					{tables.map(t => (
						<tr key={t._id}>
							<td className="font-weight-bolder">{t.slot}</td>
							<td>
								{t.name || <span className="text-muted">Mesa {t.slot}</span>}
							</td>
							<td>
								{t.connected ? (
									<span style={{ color: COLORS.green }}>● Sí</span>
								) : (
									<span style={{ color: COLORS.gray }}>○ No</span>
								)}
							</td>
							<td>
								{(t.participants || []).length
									? t.participants.join(", ")
									: "—"}
							</td>
							<td>{t.simulated ? "✔" : ""}</td>
							<td className="text-muted font-size-sm">
								{fmtDate(t.lastSeenAt)}
							</td>
							{canEdit && (
								<td className="text-right">
									<Tooltip title="Renombrar">
										<Button
											style={buttonsStyle}
											size="small"
											onClick={() => onRename(t)}
										>
											<EditIcon />
										</Button>
									</Tooltip>
									<Tooltip title="Eliminar">
										<Button
											style={buttonsStyle}
											size="small"
											onClick={() => onDelete(t)}
										>
											<DeleteIcon />
										</Button>
									</Tooltip>
								</td>
							)}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function RoundTitle({ n, roundContent, children }) {
	const rc = roundContent(n);
	return (
		<div className="mb-3">
			<h5 className="font-weight-bolder mb-0">
				Eje {n}
				{rc.title ? ` · ${rc.title}` : ""}
			</h5>
			{children}
		</div>
	);
}

/**
 * Ordena las respuestas de un eje según el orden de `tables` (por slot).
 * Las mesas desconocidas van al final.
 */
function sortResponsesByTable(tables, responses) {
	const order = new Map(tables.map((t, i) => [String(t._id), i]));
	const rank = c => {
		const i = order.get(String(c.table));
		return i === undefined ? Number.MAX_SAFE_INTEGER : i;
	};
	return [...responses].sort((a, b) => rank(a) - rank(b));
}

function ResponsesTab({
	rounds,
	tables,
	roundContent,
	tableName,
	canEdit,
	onEdit,
	onDelete
}) {
	return (
		<>
			{ROUNDS.map(n => {
				const responses = sortResponsesByTable(
					tables,
					rounds[n]?.responses || []
				);
				const rc = roundContent(n);
				return (
					<div key={n} className="mb-10">
						<RoundTitle n={n} roundContent={roundContent}>
							{rc.question && (
								<div className="font-weight-bold" style={LONG_TEXT_STYLE}>
									{rc.question}
								</div>
							)}
							<div className="text-muted font-size-sm">
								{responses.length} de {tables.length} mesas han respondido
							</div>
						</RoundTitle>
						{!responses.length ? (
							<EmptyNotice>Sin respuestas en este eje.</EmptyNotice>
						) : (
							<div className="table-responsive">
								<table className="table table-head-custom table-vertical-center">
									<thead>
										<tr>
											<th style={{ width: "180px" }}>Mesa</th>
											<th>Respuesta de la mesa</th>
											<th style={{ width: "150px" }}>Actualizada</th>
											{canEdit && <th style={{ width: "120px" }}></th>}
										</tr>
									</thead>
									<tbody>
										{responses.map(c => (
											<tr key={c._id}>
												<td className="font-weight-bolder">
													{tableName(c.table)}
												</td>
												<td>
													<div style={LONG_TEXT_STYLE}>{c.text}</div>
													{c.editedByAdmin && (
														<StateBadge
															color={COLORS.lightBlue}
															className="mt-1"
														>
															corregida
														</StateBadge>
													)}
												</td>
												<td className="text-muted font-size-sm">
													{fmtDate(c.updatedAt)}
												</td>
												{canEdit && (
													<td className="text-right">
														<Tooltip title="Corregir">
															<Button
																style={buttonsStyle}
																size="small"
																onClick={() => onEdit(c, n)}
															>
																<EditIcon />
															</Button>
														</Tooltip>
														<Tooltip title="Eliminar">
															<Button
																style={buttonsStyle}
																size="small"
																onClick={() => onDelete(c)}
															>
																<DeleteIcon />
															</Button>
														</Tooltip>
													</td>
												)}
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</div>
				);
			})}
		</>
	);
}

/** Bloque de un dato clave (apertura o eje): recuento por opción + respuesta de cada mesa. */
function SurpriseBlock({ title, surprise, data, revealed, tableName }) {
	const options = surprise.options || [];
	const counts = data.counts || [];
	const total = data.total || 0;
	const answered = data.answered || [];
	return (
		<div className="mb-10">
			<div className="mb-3">
				<h5 className="font-weight-bolder mb-0">{title}</h5>
				<div className="font-weight-bold" style={LONG_TEXT_STYLE}>
					{surprise.question || "—"}
				</div>
				{surprise.fact && (
					<div className="text-muted font-size-sm">
						{surprise.fact}
						{surprise.source ? ` (${surprise.source})` : ""}
					</div>
				)}
				<div className="text-muted font-size-sm">
					{total} respuesta(s)
					{revealed ? " · revelado" : ""}
				</div>
			</div>
			<div className="row">
				<div className="col-12 col-lg-6 mb-4">
					<table className="table table-sm table-vertical-center">
						<thead>
							<tr>
								<th>Opción</th>
								<th style={{ width: "90px" }}>Mesas</th>
								<th style={{ width: "160px" }}>%</th>
							</tr>
						</thead>
						<tbody>
							{options.map((opt, i) => {
								const c = counts[i] || 0;
								const pct = total ? Math.round((c / total) * 100) : 0;
								const correct = i === surprise.correctIndex;
								return (
									<tr
										key={i}
										style={
											correct
												? { backgroundColor: tint(COLORS.green, 0.12) }
												: undefined
										}
									>
										<td>
											{correct ? (
												<span
													className="font-weight-bolder"
													style={{ color: COLORS.green }}
												>
													✔ {opt}
												</span>
											) : (
												opt
											)}
										</td>
										<td>{c}</td>
										<td>
											<div className="d-flex align-items-center">
												<div
													className="progress flex-grow-1 mr-2"
													style={{ height: "6px" }}
												>
													<div
														className="progress-bar"
														style={{
															width: `${pct}%`,
															backgroundColor: correct
																? COLORS.green
																: COLORS.blue
														}}
													/>
												</div>
												<span className="font-size-sm">{pct}%</span>
											</div>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
				<div className="col-12 col-lg-6 mb-4">
					{!answered.length ? (
						<EmptyNotice>Ninguna mesa ha respondido.</EmptyNotice>
					) : (
						<table className="table table-sm table-vertical-center">
							<thead>
								<tr>
									<th>Mesa</th>
									<th>Respuesta</th>
									<th style={{ width: "90px" }}>Acierto</th>
								</tr>
							</thead>
							<tbody>
								{answered.map((a, i) => {
									const ok = a.optionIndex === surprise.correctIndex;
									return (
										<tr key={`${a.table}-${i}`}>
											<td className="font-weight-bolder">
												{tableName(a.table)}
											</td>
											<td>{options[a.optionIndex] ?? a.optionIndex}</td>
											<td>
												{ok ? (
													<span style={{ color: COLORS.green }}>✔ Sí</span>
												) : (
													<span style={{ color: COLORS.gray }}>✘ No</span>
												)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					)}
				</div>
			</div>
		</div>
	);
}

function SurpriseTab({ rounds, content, roundContent, tableName }) {
	const opening = rounds[OPENING_ROUND] || {};
	return (
		<>
			<SurpriseBlock
				title="Apertura"
				surprise={content.opening?.surprise || {}}
				data={opening.surprise || {}}
				revealed={!!opening.revealed}
				tableName={tableName}
			/>
			{ROUNDS.map(n => {
				const rc = roundContent(n);
				return (
					<SurpriseBlock
						key={n}
						title={`Eje ${n}${rc.title ? ` · ${rc.title}` : ""}`}
						surprise={rc.surprise || {}}
						data={rounds[n]?.surprise || {}}
						revealed={!!rounds[n]?.revealed}
						tableName={tableName}
					/>
				);
			})}
		</>
	);
}

function ExpectationsTab({ question, items, total, canEdit, onDelete }) {
	return (
		<>
			<div className="mb-6">
				<h5 className="font-weight-bolder mb-0">Pregunta de inicio</h5>
				<div className="font-weight-bold" style={LONG_TEXT_STYLE}>
					{question || "—"}
				</div>
				<div className="text-muted font-size-sm">
					{total} respuesta(s) · recogidas por QR antes de la dinámica
				</div>
			</div>
			{!items.length ? (
				<EmptyNotice>
					Todavía no hay respuestas a la pregunta de inicio.
				</EmptyNotice>
			) : (
				<div className="table-responsive">
					<table className="table table-head-custom table-vertical-center">
						<thead>
							<tr>
								<th style={{ width: "60px" }}>#</th>
								<th style={{ width: "150px" }}>Fecha</th>
								<th>Expectativas</th>
								{canEdit && <th style={{ width: "60px" }}></th>}
							</tr>
						</thead>
						<tbody>
							{items.map((e, i) => (
								<tr key={e._id}>
									<td className="text-muted">{i + 1}</td>
									<td className="text-muted font-size-sm">
										{fmtDate(e.createdAt)}
									</td>
									<td>
										<div style={LONG_TEXT_STYLE}>{e.text}</div>
									</td>
									{canEdit && (
										<td className="text-right">
											<Tooltip title="Eliminar">
												<Button
													style={buttonsStyle}
													size="small"
													onClick={() => onDelete(e)}
												>
													<DeleteIcon />
												</Button>
											</Tooltip>
										</td>
									)}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</>
	);
}

function SurveyTab({ questions, responses, stats, canEdit, onDelete }) {
	return (
		<>
			<div className="mb-6">
				<SurveySummary stats={stats} compact />
			</div>
			<h6 className="font-weight-bolder mb-3">Respuestas individuales</h6>
			{!responses.length ? (
				<EmptyNotice>Todavía no hay respuestas de la encuesta.</EmptyNotice>
			) : (
				<div className="table-responsive">
					<table className="table table-head-custom table-vertical-center">
						<thead>
							<tr>
								<th style={{ width: "150px" }}>Fecha</th>
								{questions.map(q => (
									<th
										key={q.key}
										title={questionLabel(q)}
										style={{ minWidth: q.type === "text" ? "260px" : "120px" }}
									>
										{questionLabel(q)}
									</th>
								))}
								{canEdit && <th style={{ width: "60px" }}></th>}
							</tr>
						</thead>
						<tbody>
							{responses.map(r => (
								<tr key={r._id}>
									<td className="text-muted font-size-sm">
										{fmtDate(r.createdAt)}
									</td>
									{questions.map(q => (
										<td key={q.key}>
											{r.answers?.[q.key] !== undefined &&
											r.answers?.[q.key] !== null &&
											r.answers?.[q.key] !== "" ? (
												q.type === "text" ? (
													<div style={LONG_TEXT_STYLE}>
														{String(r.answers[q.key])}
													</div>
												) : (
													String(r.answers[q.key])
												)
											) : (
												<span className="text-muted">—</span>
											)}
										</td>
									))}
									{canEdit && (
										<td className="text-right">
											<Tooltip title="Eliminar">
												<Button
													style={buttonsStyle}
													size="small"
													onClick={() => onDelete(r)}
												>
													<DeleteIcon />
												</Button>
											</Tooltip>
										</td>
									)}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</>
	);
}
